import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, FileText, Users, FolderKanban, Receipt, ArrowRight, Command } from "lucide-react";
import { usePrefetch } from "../hooks/usePrefetch";
import { useApp } from "../AppContext";

/**
 * Cmd/Ctrl+K command palette.
 * - Searches across clients, quotes, invoices, projects (locally on prefetched cache).
 * - Supports keyboard navigation (↑ ↓ Enter Esc).
 * - Triggers also from a "/" key when not focusing an input.
 */
export default function CommandPalette({ open, onClose }) {
  const { lang } = useApp();
  const { clients, projects, quotes, invoices } = usePrefetch();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => { if (open) { setQ(""); setActive(0); setTimeout(() => inputRef.current?.focus(), 50); } }, [open]);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) {
      // Show top recent items when empty
      return [
        ...quotes.slice(0, 4).map((x) => ({ kind: "quote", id: x.id, label: x.project_name, sub: `#${x.number || ""} · ${(x.total ?? 0).toFixed(2)} $`, to: `/app/quotes/${x.id}` })),
        ...invoices.slice(0, 3).map((x) => ({ kind: "invoice", id: x.id, label: `Facture ${x.number || x.id.slice(0, 6)}`, sub: `${(x.total ?? 0).toFixed(2)} $`, to: `/app/invoices/${x.id}` })),
        ...clients.slice(0, 3).map((x) => ({ kind: "client", id: x.id, label: x.name, sub: x.company || x.email || "", to: `/app/clients` })),
      ].slice(0, 8);
    }
    const items = [];
    for (const c of clients) {
      if ((c.name || "").toLowerCase().includes(needle) || (c.company || "").toLowerCase().includes(needle) || (c.email || "").toLowerCase().includes(needle))
        items.push({ kind: "client", id: c.id, label: c.name, sub: c.company || c.email || "", to: `/app/clients` });
    }
    for (const p of projects) {
      if ((p.name || "").toLowerCase().includes(needle) || (p.address || "").toLowerCase().includes(needle))
        items.push({ kind: "project", id: p.id, label: p.name, sub: p.address || "", to: `/app/projects` });
    }
    for (const q2 of quotes) {
      if ((q2.project_name || "").toLowerCase().includes(needle) || (q2.client_name || "").toLowerCase().includes(needle) || (q2.number || "").toLowerCase().includes(needle))
        items.push({ kind: "quote", id: q2.id, label: q2.project_name, sub: `${q2.client_name || ""} · ${(q2.total ?? 0).toFixed(2)} $`, to: `/app/quotes/${q2.id}` });
    }
    for (const inv of invoices) {
      if ((inv.number || "").toLowerCase().includes(needle) || (inv.client_name || "").toLowerCase().includes(needle))
        items.push({ kind: "invoice", id: inv.id, label: `Facture ${inv.number || inv.id.slice(0, 6)}`, sub: `${inv.client_name || ""} · ${(inv.total ?? 0).toFixed(2)} $`, to: `/app/invoices/${inv.id}` });
    }
    return items.slice(0, 12);
  }, [q, clients, projects, quotes, invoices]);

  const totalRows = results.length + 4; // 4 quick actions
  const onKey = (e) => {
    if (e.key === "Escape") { onClose(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % totalRows); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + totalRows) % totalRows); }
    if (e.key === "Enter") {
      e.preventDefault();
      const quickActions = [
        { to: "/app/quotes/new" }, { to: "/app/quotes" }, { to: "/app/invoices" }, { to: "/app/clients" },
      ];
      if (active < 4) { nav(quickActions[active].to); onClose(); return; }
      const r = results[active - 4];
      if (r) { nav(r.to); onClose(); }
    }
  };

  if (!open) return null;
  const isFr = lang === "fr";
  const icons = { client: Users, quote: FileText, invoice: Receipt, project: FolderKanban };
  const quickActions = [
    { label: isFr ? "Nouveau devis" : "New quote", to: "/app/quotes/new", kbd: "N" },
    { label: isFr ? "Voir tous les devis" : "All quotes", to: "/app/quotes", kbd: "Q" },
    { label: isFr ? "Voir les factures" : "Invoices", to: "/app/invoices", kbd: "F" },
    { label: isFr ? "Gérer les clients" : "Clients", to: "/app/clients", kbd: "C" },
  ];

  return (
    <div onClick={onClose} className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }}
      data-testid="cmdk-overlay">
      <div onClick={(e) => e.stopPropagation()} onKeyDown={onKey} tabIndex={-1}
        className="w-full max-w-2xl rounded-2xl overflow-hidden"
        style={{ background: "#0F0F10", border: "1px solid rgba(212,175,55,0.25)", boxShadow: "0 30px 80px -10px rgba(0,0,0,0.8)" }}
        data-testid="cmdk-modal">
        <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: "rgba(212,175,55,0.15)" }}>
          <Search size={18} color="#D4AF37" />
          <input ref={inputRef} value={q} onChange={(e) => { setQ(e.target.value); setActive(0); }}
            placeholder={isFr ? "Rechercher devis, factures, clients, projets..." : "Search quotes, invoices, clients, projects..."}
            className="flex-1 bg-transparent outline-none text-sm" style={{ color: "#F5F5F5" }}
            data-testid="cmdk-input" />
          <kbd className="text-[10px] px-2 py-1 rounded" style={{ background: "rgba(245,245,245,0.06)", color: "rgba(245,245,245,0.5)" }}>ESC</kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto py-2">
          <div className="px-4 py-2 text-[10px] uppercase tracking-widest" style={{ color: "rgba(245,245,245,0.4)" }}>
            {isFr ? "Actions rapides" : "Quick actions"}
          </div>
          {quickActions.map((a, i) => (
            <button key={a.to} onClick={() => { nav(a.to); onClose(); }} onMouseEnter={() => setActive(i)}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm"
              style={{ background: active === i ? "rgba(212,175,55,0.08)" : "transparent", color: "#F5F5F5" }}
              data-testid={`cmdk-action-${i}`}>
              <ArrowRight size={14} color="#D4AF37" />
              <span className="flex-1">{a.label}</span>
              <kbd className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "rgba(245,245,245,0.06)", color: "rgba(245,245,245,0.6)" }}>{a.kbd}</kbd>
            </button>
          ))}
          {results.length > 0 && (
            <div className="px-4 py-2 text-[10px] uppercase tracking-widest mt-2" style={{ color: "rgba(245,245,245,0.4)" }}>
              {q ? (isFr ? "Résultats" : "Results") : (isFr ? "Récents" : "Recent")}
            </div>
          )}
          {results.map((r, i) => {
            const Icon = icons[r.kind] || FileText;
            const idx = i + 4;
            return (
              <button key={`${r.kind}-${r.id}`} onClick={() => { nav(r.to); onClose(); }} onMouseEnter={() => setActive(idx)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm"
                style={{ background: active === idx ? "rgba(212,175,55,0.08)" : "transparent", color: "#F5F5F5" }}
                data-testid={`cmdk-result-${i}`}>
                <Icon size={14} color="#D4AF37" />
                <div className="flex-1 min-w-0">
                  <div className="truncate">{r.label}</div>
                  <div className="text-[11px] truncate" style={{ color: "rgba(245,245,245,0.5)" }}>{r.sub}</div>
                </div>
                <span className="text-[10px] uppercase tracking-widest" style={{ color: "rgba(212,175,55,0.6)" }}>{r.kind}</span>
              </button>
            );
          })}
          {q && results.length === 0 && (
            <div className="px-4 py-6 text-center text-sm" style={{ color: "rgba(245,245,245,0.5)" }} data-testid="cmdk-empty">
              {isFr ? "Aucun résultat" : "No results"}
            </div>
          )}
        </div>
        <div className="px-4 py-2 flex items-center justify-between text-[10px] border-t" style={{ borderColor: "rgba(212,175,55,0.1)", color: "rgba(245,245,245,0.4)" }}>
          <span className="flex items-center gap-2">
            <Command size={10} /> {isFr ? "Recherche globale" : "Global search"}
          </span>
          <span>↑↓ {isFr ? "naviguer" : "navigate"} · ↵ {isFr ? "ouvrir" : "open"}</span>
        </div>
      </div>
    </div>
  );
}
